import LoadingIndicator, { initLoadingIndicatorsByName } from '../loadingindicator';
import { isMapMode } from '../maps/util';
import { yieldToMainThread } from '../yield';
import { FilterCount, countFilters } from './filter-count';
import PropertyFilterPopup from './filter-popup';
import { locationFilters, resetSearchForm } from './filters';
import { updateFilterCallToActionButtonsVisibility } from './location-filters';
import { updatePropertyTypes, updateTagsValidityOnForm } from './search-filters';

class PopupPropertySearchForm {
    _popupCtx: PropertyFilterPopup;
    _form: HTMLFormElement;
    _baseForm: HTMLFormElement;
    _keepCount: boolean;
    _keepCountEndpoint: URL;
    _locationFiltersContainer: HTMLFieldSetElement;
    _radiusSelectContainer: HTMLElement;
    _radiusSelect: HTMLSelectElement;
    _locationIDInput: HTMLInputElement;
    _resetBtns: NodeListOf<HTMLButtonElement>;
    _primaryResetBtn: HTMLButtonElement;
    _loadingIndicators: Array<LoadingIndicator> = [];

    constructor( popupCtx: PropertyFilterPopup, form: HTMLFormElement, baseForm: HTMLFormElement ) {
        this._popupCtx = popupCtx;
        this._form = form;
        this._baseForm = baseForm;

        if ( this._form.dataset.keepCount && this._form.dataset.keepCount === 'true' && this._form.dataset.keepCountEndpoint ) {
            this._keepCount = true;
            this._keepCountEndpoint = new URL( window.location.origin + this._form.dataset.keepCountEndpoint );
        }

        this._radiusSelectContainer = this._form.querySelector( '.searchpopup__location-radius' ) as HTMLElement;
        this._radiusSelect = this._form.querySelector( 'select[name="radius"]' ) as HTMLSelectElement;
        this._locationIDInput = this._form.querySelector( '[name="locationId"]' ) as HTMLInputElement;
        this._resetBtns = this._form.querySelectorAll<HTMLButtonElement>( 'button[type="reset"]' );
        this._primaryResetBtn = this._form.querySelector( '#filter-popup-reset-btn' ) as HTMLButtonElement;

        this._loadingIndicators = initLoadingIndicatorsByName( 'inline-search-form' );
        this._init();
    }

    _init(): void {
        this._form.addEventListener( 'submit', this._onSubmit.bind( this ) );

        this._form.addEventListener( 'change', this._onChange.bind( this ) );

        this._form.addEventListener( 'twc:typeahead:value-selected', this._onTypeaheadSelect.bind( this ) );

        this._resetBtns.forEach( ( btn ) => btn.addEventListener( 'click', this._onReset.bind( this ) ) );

        document.addEventListener( 'twc:update-filter-count-badges', this.updateFilterCountBasedState.bind( this ) );

        this.updateFilterCountBasedState();

        window.addEventListener( 'pageshow', ( event: PageTransitionEvent ) => {
            if ( event.persisted ) {
                this._stopLoading();
            }
        } );
    }

    async _onSubmit( event: SubmitEvent ): Promise<void> {
        if ( !event.submitter ) {
            return;
        }

        this._startLoading();

        const btnThatTriggeredSubmit = event.submitter as HTMLButtonElement;

        const mapMode = isMapMode();

        const destination: string = ( btnThatTriggeredSubmit.dataset.destination || '' ) as string;

        if ( destination ) {
            let destinationInput: HTMLInputElement | null = this._form.querySelector( 'input[name="destination"]' );

            if ( !destinationInput ) {
                destinationInput = document.createElement( 'input' );
                destinationInput.name = 'destination';
                destinationInput.type = 'hidden';
                this._form.appendChild( destinationInput );
            }

            destinationInput.value = destination;

            if ( mapMode ) {
                switch ( destination ) {
                case 'draw-your-search': {
                    const area = this._form.querySelector<HTMLInputElement>( 'input[name=area]' );

                    if ( area && area.value.length > 0 ) {
                        this._popupCtx.close();
                    } else {
                        btnThatTriggeredSubmit.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:activate', { bubbles: true } ) );
                    }
                    break;
                }
                case 'current-location':
                    btnThatTriggeredSubmit.dispatchEvent( new CustomEvent( 'twc:mapsearch:geolocate-activate', { bubbles: true } ) );
                    break;
                }
            }

            this._stopLoading();
            return;
        }

        if ( !destination && mapMode ) {
            this._stopLoading();
        }

        this._popupCtx.close( true );

        await yieldToMainThread();
    }

    _startLoading(): void {
        this._loadingIndicators.forEach( async ( indicator: LoadingIndicator ) => {
            indicator.show();
            await yieldToMainThread();
        } );
    }

    _stopLoading(): void {
        this._loadingIndicators.forEach( async ( indicator: LoadingIndicator ) => {
            indicator.hide();
            await yieldToMainThread();
        } );
    }

    _onChange( event: Event ): void {
        this.updateFilterCountBasedState();
        updateFilterCallToActionButtonsVisibility( this._form );
    }

    async _onReset( event: Event ): Promise<void> {
        if ( !event.target ) {
            return;
        }

        event.preventDefault();

        this._primaryResetBtn.disabled = true;

        resetSearchForm( this._form, [ 'locationId', 'locationFreeText' ] );

        if ( this._locationIDInput && this._locationIDInput.value.length > 0 ) {
            this._radiusSelectContainer.classList.add( 'd-none' );
            this._radiusSelect.disabled = true;
            this._radiusSelect.value = '';
            this._radiusSelect.classList.remove( 'has-value' );

            this._locationIDInput.dispatchEvent( new CustomEvent( 'twc:typeahead:set-value', {
                bubbles: true,
                detail: {
                    label: '',
                    state: {
                        value: '',
                    },
                },
            } ) );
        }

        this.updateFilterCountBasedState();

        updateFilterCallToActionButtonsVisibility( this._form );

        updateTagsValidityOnForm( this._form );

        updatePropertyTypes( this._form );

        event.target.dispatchEvent( new CustomEvent( 'twc:update-search-form-count', {
            bubbles: true,
        } ) );
    }

    async _onTypeaheadSelect( event: CustomEvent ): Promise<void> {
        const value = event.detail.value;

        if ( !value.startsWith( 'location-filters' ) ) {
            locationFilters.all().forEach( ( name: string ) => {
                this._form.querySelectorAll( `input[name="${name}"]` ).forEach( ( element: HTMLInputElement ) => element.value = '' );
            } );
        }

        updateFilterCallToActionButtonsVisibility( this._form );

        await yieldToMainThread();

        this.updateFilterCountBasedState();

        await yieldToMainThread();
    }

    updateFilterCountBasedState(): void {
        const filterCounts: FilterCount = countFilters( this._form );

        this._form.querySelectorAll( '.filtercount' ).forEach( ( counter: HTMLElement ) => {
            counter.innerHTML = filterCounts.property.count.toString();

            if ( filterCounts.property.count > 0 ) {
                counter.classList.remove( 'd-none' );
            } else {
                counter.classList.add( 'd-none' );
            }
        } );

        if ( filterCounts.property.count > 0 ) {
            this._primaryResetBtn.disabled = false;
        } else {
            this._primaryResetBtn.disabled = true;
        }
    }
}

export default PopupPropertySearchForm;
